import {Search, X, SlidersHorizontal, Calendar} from 'lucide-react'
import {useState} from 'react'
import type {Priority} from '../../types'
import {dueDateOptions, type SharedTaskFilters} from '../board/taskFilters'
import {useApp} from '../../context/AppContext'
import {getPriorityConfig, getStatusConfig} from '../../utils/helpers'
import Avatar from '../common/Avatar'

export interface TaskFilters extends SharedTaskFilters {
  boardIds: string[]
  hasSubtasks: 'all' | 'yes' | 'no'
  pendingOnly: boolean
}

export const defaultFilters: TaskFilters = {
  search: '',
  status: [],
  priority: [],
  assigneeIds: [],
  boardIds: [],
  labelIds: [],
  dueDateRange: 'all',
  hasSubtasks: 'all',
  pendingOnly: false,
  assignment: 'all',
}

interface TasksFiltersProps {
  filters: TaskFilters
  onChange: (filters: TaskFilters) => void
  allStatuses: string[]
}

const priorities: Priority[] = ['low', 'medium', 'high', 'critical']

export default function TasksFilters({filters, onChange, allStatuses}: TasksFiltersProps) {
  const {state} = useApp()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const activeFilterCount = [
    Boolean(filters.search.trim()),
    filters.status.length > 0,
    filters.priority.length > 0,
    filters.assigneeIds.length > 0,
    filters.boardIds.length > 0,
    filters.labelIds.length > 0,
    filters.dueDateRange !== 'all',
    filters.hasSubtasks !== 'all',
    filters.assignment !== 'all',
    filters.pendingOnly,
  ].filter(Boolean).length

  const update = (partial: Partial<TaskFilters>) => {
    onChange({...filters, ...partial})
  }

  const toggleArrayFilter = <K extends keyof TaskFilters>(key: K, value: string) => {
    const current = filters[key] as string[]
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    update({
      [key]: next,
      ...(key === 'assigneeIds' ? {assignment: 'all'} : {}),
    } as Partial<TaskFilters>)
  }

  const clearAll = () => onChange(defaultFilters)

  return (
    <div className='devops-filter-panel space-y-3'>
      <div className='flex flex-col sm:flex-row gap-3'>
        <div className='flex-1 relative'>
          <Search size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
          <input
            type='text'
            value={filters.search}
            onChange={(e) => update({search: e.target.value})}
            placeholder='Buscar tarefas por título ou descrição...'
            className='w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all placeholder:text-slate-400'
            aria-label='Buscar tarefas'
          />
          {filters.search && (
            <button
              onClick={() => update({search: ''})}
              className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
              aria-label='Limpar busca'
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className='flex gap-2'>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-expanded={showAdvanced}
            aria-controls='tasks-advanced-filters'
            aria-label='Filtros avançados'
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
              showAdvanced || activeFilterCount > 0
                ? 'bg-brand-50 border-brand-200 text-brand-600'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={16} />
            <span className='hidden sm:inline'>Filtros</span>
            {activeFilterCount > 0 && (
              <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-500 text-white text-xs font-bold'>
                {activeFilterCount}
              </span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={clearAll}
              className='inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors'
            >
              <X size={14} />
              <span>Limpar</span>
            </button>
          )}
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-xs font-medium text-slate-500'>Mostrar</span>
        {(
          [
            {value: 'all', label: 'Todas'},
            {value: 'mine', label: 'Minhas tarefas'},
            {value: 'unassigned', label: 'Sem responsável'},
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type='button'
            aria-pressed={filters.assignment === option.value}
            disabled={option.value === 'mine' && !state.currentUser}
            onClick={() => update({assignment: option.value, assigneeIds: []})}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-40 ${
              filters.assignment === option.value
                ? 'bg-brand-50 text-brand-700 border-brand-200'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {option.label}
          </button>
        ))}
        <select
          aria-label='Filtrar por prazo'
          value={filters.dueDateRange}
          onChange={(e) =>
            update({dueDateRange: e.target.value as SharedTaskFilters['dueDateRange']})
          }
          className='rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700'
        >
          {dueDateOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <label className='inline-flex items-center gap-2 text-xs font-medium text-slate-600'>
        <input
          type='checkbox'
          checked={filters.pendingOnly}
          onChange={(event) => update({pendingOnly: event.target.checked})}
          className='h-4 w-4 rounded border-slate-300 accent-brand-600'
        />
        Somente pendentes
      </label>

      <div className='flex flex-wrap gap-2'>
        {allStatuses.map((s) => {
          const config = getStatusConfig(s)
          const active = filters.status.includes(s)
          return (
            <button
              key={s}
              aria-pressed={active}
              onClick={() => toggleArrayFilter('status', s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                active
                  ? `${config.color} border-current shadow-sm`
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {config.label}
            </button>
          )
        })}
      </div>

      {showAdvanced && (
        <div
          id='tasks-advanced-filters'
          className='bg-slate-50/80 rounded-2xl border border-slate-200 p-4 space-y-4 animate-fade-in'
        >
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            <FilterSection label='Prioridade'>
              <div className='flex flex-wrap gap-1.5'>
                {priorities.map((p) => {
                  const config = getPriorityConfig(p)
                  const active = filters.priority.includes(p)
                  return (
                    <button
                      key={p}
                      aria-pressed={active}
                      onClick={() => toggleArrayFilter('priority', p)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? `${config.color} border-current`
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {config.label}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Quadro'>
              <div className='flex flex-wrap gap-1.5'>
                {state.boards.map((board) => {
                  const active = filters.boardIds.includes(board.id)
                  return (
                    <button
                      key={board.id}
                      aria-pressed={active}
                      onClick={() => toggleArrayFilter('boardIds', board.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'bg-brand-50 text-brand-600 border-brand-200'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {board.name}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Prazo'>
              <div className='flex flex-wrap gap-1.5'>
                {dueDateOptions.map((opt) => (
                  <button
                    key={opt.value}
                    aria-pressed={filters.dueDateRange === opt.value}
                    onClick={() => update({dueDateRange: opt.value})}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                      filters.dueDateRange === opt.value
                        ? 'bg-brand-50 text-brand-600 border-brand-200'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {opt.value === 'overdue' && <Calendar size={12} />}
                    {opt.label}
                  </button>
                ))}
              </div>
            </FilterSection>

            <FilterSection label='Responsável'>
              <div className='flex flex-wrap gap-1.5 max-h-32 overflow-y-auto'>
                {state.users.map((user) => {
                  const active = filters.assigneeIds.includes(user.id)
                  return (
                    <button
                      key={user.id}
                      aria-pressed={active}
                      onClick={() => toggleArrayFilter('assigneeIds', user.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'bg-brand-50 text-brand-600 border-brand-200'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <Avatar name={user.name} color={user.avatarColor} size='xs' />
                      {user.name.split(' ')[0]}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Etiquetas'>
              <div className='flex flex-wrap gap-1.5 max-h-32 overflow-y-auto'>
                {state.labels.map((label) => {
                  const active = filters.labelIds.includes(label.id)
                  return (
                    <button
                      key={label.id}
                      aria-pressed={active}
                      onClick={() => toggleArrayFilter('labelIds', label.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'text-white border-transparent shadow-sm'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                      style={active ? {backgroundColor: label.color} : undefined}
                    >
                      <span className='flex items-center gap-1.5'>
                        <span
                          className='w-2 h-2 rounded-full shrink-0'
                          style={{backgroundColor: label.color}}
                        />
                        {label.name}
                      </span>
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Subtarefas'>
              <div className='flex gap-1.5'>
                {(
                  [
                    {value: 'all', label: 'Todas'},
                    {value: 'yes', label: 'Com subtarefas'},
                    {value: 'no', label: 'Sem subtarefas'},
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    aria-pressed={filters.hasSubtasks === opt.value}
                    onClick={() => update({hasSubtasks: opt.value})}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                      filters.hasSubtasks === opt.value
                        ? 'bg-brand-50 text-brand-600 border-brand-200'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </FilterSection>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterSection({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <div>
      <p className='text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2'>{label}</p>
      {children}
    </div>
  )
}
